import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { BEResponse, MetaResponse } from '@qtek/core/api-core';
import { EMPTY, pipe } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import {
  MetaCoreState,
  metaCoreInitialState,
  ServerStatus,
  ServerTarget,
} from '../models';
import { MetaCoreService, ServerStatusService } from '../services';
import { applyMeta } from './meta-core.updaters';

const SERVER_TARGETS: ServerTarget[] = ['http', 'ws'];

export const MetaCoreStore = signalStore(
  { providedIn: 'root' },
  withState(metaCoreInitialState),
  withMethods(
    (
      store,
      metaCoreService = inject(MetaCoreService),
      serverStatusService = inject(ServerStatusService)
    ) => {
      const loadMeta = (params: string[]): void => {
        metaCoreService.requestMetaData(params);
      };

      return {
        _applyMetaResponses: rxMethod<BEResponse<MetaResponse>>(
          pipe(
            tap((response: BEResponse<MetaResponse>) =>
              patchState(store, applyMeta(response.res))
            )
          )
        ),
        loadPortalMeta: (): void => loadMeta(['ptl']),
        loadLanguages: (): void => loadMeta(['lngs']),
        loadWebsocket: (): void => loadMeta(['wsPingPong']),
        loadColors: (): void => loadMeta(['colors']),
        setWebsocketVersion(websocketVersion: string): void {
          patchState(store, { websocketVersion });
        },
        setWebsocketPingPongDelay(wsPingPong: number): void {
          patchState(store, { wsPingPong });
        },
        loadServerStatus: rxMethod<void>(
          pipe(
            mergeMap(() => SERVER_TARGETS),
            mergeMap(target =>
              serverStatusService.getServerStatus(target).pipe(
                tap(status => patchState(store, statusPatch(target, status))),
                catchError(() => {
                  patchState(store, statusPatch(target, null));
                  return EMPTY;
                })
              )
            )
          )
        ),
      };
    }
  ),
  withHooks(store => ({
    onInit(): void {
      store._applyMetaResponses(inject(MetaCoreService).metaDataSuccess$);
    },
  }))
);

function statusPatch(
  target: ServerTarget,
  status: ServerStatus | null
):
  | Pick<MetaCoreState, 'httpServerStatus'>
  | Pick<MetaCoreState, 'wsServerStatus'> {
  return target === 'http'
    ? { httpServerStatus: status }
    : { wsServerStatus: status };
}
